import React, { ReactNode } from 'react';
import BasicInner from 'components/Inner/Inner';
import styled, { css } from 'styled-components/macro';
import Breakpoints from 'themes/constants/breakpoints';

// Styles
const Wrap = styled.div<{
  isDark?: boolean,
  background?: string,
  withPaddings?: boolean,
  withShadows?: boolean,
}>`
    position: relative;
    max-width: 100vw;
    width: 100%;
    overflow: hidden;
    background: ${({ theme, background, isDark }) => (background || (isDark ? theme.palette.primaryDark : 'transparent'))} fixed no-repeat;
    padding: ${({ withPaddings }) => (withPaddings ? '23px 0' : '0')};
    @media only screen and (${Breakpoints.TABLET}) {
        padding: ${({ withPaddings }) => (withPaddings ? '27px 0' : '0')};
    }
    @media only screen and (${Breakpoints.LAPTOP}) {
        padding: ${({ withPaddings }) => (withPaddings ? '43px 0' : '0')};
        &:before,
        &:after {
            position: absolute;
            top: 0;
            bottom: 0;
            content: "";
            display: ${({ withShadows }) => (withShadows ? 'block' : 'none')};
            width: ${({ theme }) => `calc((100vw - ${theme.width.wrap}) / 2)`};
            height: 100%;
            background: ${({ theme, background, isDark }) => (isDark
    ? theme.palette.primaryDark
    : (background || theme.background.primary))} fixed no-repeat;
            opacity: 0.9;
            filter: blur(80px);
            pointer-events: none;
            z-index: 10000;
        }
        &:before {
            left: 0;
            background-position: left;
        }
        &:after {
            right: 0;
            background-position: right;
        }
    };
`;
const Inner = styled(BasicInner)<{ backgroundMask?: string }>`
    position: relative;
    ${({ backgroundMask }) => backgroundMask && css`
        &:after {
            content: "";
            position: absolute;
            top: 0;
            bottom: 0;
            left: ${({ theme }) => theme.paddings.inner.mobile.paddingLeft};
            right: ${({ theme }) => theme.paddings.inner.mobile.paddingRight};
            background: ${backgroundMask};
            background-size: contain;
        }
    `};
    @media only screen and (${Breakpoints.TABLET}) {
        ${({ backgroundMask }) => backgroundMask && css`
            &:after {
                left: ${({ theme }) => theme.paddings.inner.tablet.paddingLeft};
                right: ${({ theme }) => theme.paddings.inner.tablet.paddingRight};
            }
        `};
    }
    @media only screen and (${Breakpoints.LAPTOP}) {
        ${({ backgroundMask }) => backgroundMask && css`
            &:after {
                left: ${({ theme }) => theme.paddings.inner.desktop.paddingLeft};
                right: ${({ theme }) => theme.paddings.inner.desktop.paddingRight};
            }
        `};
    }
`;

// Interfaces
type IChild = ReactNode | ReactNode[] | null;
interface ISectionsContent {
  children: IChild | IChild[],
  isDark?: boolean,
  background?: string,
  withPaddings?: boolean,
  withInner?: boolean,
  withInnerPaddings?: boolean,
  withShadows?: boolean,
  backgroundMask?: string,
  className?: string,
}

// Component
const SectionsContent = (props: ISectionsContent) => {
  const {
    children,
    isDark,
    background,
    backgroundMask,
    withPaddings = true,
    withInner = true,
    withInnerPaddings = true,
    withShadows = true,
    className,
  } = props;

  const childrenEls = withInner ? (
    <Inner
      backgroundMask={backgroundMask}
      withPaddings={withInnerPaddings}
    >
      {children}
    </Inner>
  ) : children;

  return (
    <Wrap
      isDark={isDark}
      background={background}
      withPaddings={withPaddings}
      withShadows={withShadows}
      className={className}
    >
      {childrenEls}
    </Wrap>
  );
};

SectionsContent.defaultProps = {
  isDark: false,
  background: '',
  backgroundMask: '',
  className: '',
  withShadows: true,
  withPaddings: true,
  withInner: true,
  withInnerPaddings: true,
};

export default SectionsContent;
